import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, uploadImage, uploadFile, uploadFileChunked } from "@/lib/api";
import { currency } from "@/components/Primitives";
import ShareCard from "@/components/dashboard/ShareCard";
import ReportCard from "@/components/dashboard/ReportCard";
import { ArrowLeft, Save, Copy, Check, ExternalLink, Plus, X, Trash2, Sparkles, Upload, Download, FileText } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
const lbl = "text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755] block mb-1";

export default function ListingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [agents, setAgents] = useState([]);
  const [featInput, setFeatInput] = useState("");
  const [photoInput, setPhotoInput] = useState("");
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importingPhotos, setImportingPhotos] = useState(false);
  const [ohDate, setOhDate] = useState("");
  const [ohTime, setOhTime] = useState("");

  const downloadFlyer = async (kind) => {
    try {
      const params = kind === "open-house" ? { date: ohDate, time: ohTime } : {};
      const res = await api.get(`/flyers/${id}/${kind}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${form.slug || "listing"}-${kind}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Flyer downloaded");
    } catch { toast.error("Couldn't generate the flyer. Save the listing first, then try again."); }
  };

  const doImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post("/lm/import", { url: importUrl.trim() });
      setForm((f) => {
        const merged = { ...f, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && !v.length))) };
        if (!f.hero_photo && Array.isArray(data.photos) && data.photos.length) merged.hero_photo = data.photos[0];
        return merged;
      });
      if (data.address_line && !slugEdited) setForm((f) => ({ ...f, slug: slugify(data.address_line) }));
      toast.success("Imported — review the details, then Save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not import that URL"); }
    finally { setImporting(false); }
  };

  const load = useCallback(async () => {
    const [p, a] = await Promise.all([api.get(`/lm/properties/${id}`), api.get("/lm/roster")]);
    setForm(p.data); setAgents(a.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const publicUrl = `${window.location.origin}/p/${form.slug}`;
  const videoAbs = form.video_walkthrough ? `${process.env.REACT_APP_BACKEND_URL}${form.video_walkthrough}` : "";

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = { ...form, ...overrides, price: num(overrides.price ?? form.price), beds: num(form.beds), baths: num(form.baths), sqft: num(form.sqft), sold_price: num(form.sold_price) };
      const { data } = await api.put(`/lm/properties/${id}`, payload);
      setForm(data); toast.success("Listing saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const togglePublish = () => {
    if (form.status !== "published") {
      const missing = [];
      if (!(form.photos || []).length) missing.push("at least one photo");
      if (!form.price) missing.push("a price");
      if (!form.description) missing.push("a description");
      if (missing.length) {
        toast.error(`Add ${missing.join(", ")} before publishing.`);
        return;
      }
    }
    save({ status: form.status === "published" ? "draft" : "published" });
  };
  const del = async () => { if (!window.confirm("Delete this listing permanently?")) return; await api.delete(`/lm/properties/${id}`); toast.success("Deleted"); navigate("/listings"); };
  const addFeature = () => { const v = featInput.trim(); if (!v) return; set("features", [...(form.features || []), v]); setFeatInput(""); };
  const isDirectImage = (u) => /\.(jpe?g|png|webp|gif|avif|svg)(\?.*)?$/i.test(u) || u.startsWith("data:image/");
  const addPhoto = async () => {
    const v = photoInput.trim();
    if (!v) return;
    if (isDirectImage(v)) {
      const photos = [...(form.photos || []), v];
      set("photos", photos);
      if (!form.hero_photo) set("hero_photo", v);
      setPhotoInput("");
      return;
    }
    setImportingPhotos(true);
    try {
      const { data } = await api.post("/lm/import-photos", { url: v });
      const incoming = (data.photos || []).filter((u) => !(form.photos || []).includes(u));
      if (!incoming.length) { toast.info("No new photos found on that link"); return; }
      setForm((s) => { const photos = [...(s.photos || []), ...incoming]; return { ...s, photos, hero_photo: s.hero_photo || photos[0] }; });
      toast.success(`Imported ${incoming.length} photo${incoming.length > 1 ? "s" : ""}`);
      setPhotoInput("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't import photos from that link");
    } finally { setImportingPhotos(false); }
  };
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const url = await uploadImage(f, "/lm/upload"); setForm((s) => ({ ...s, photos: [...(s.photos || []), url], hero_photo: s.hero_photo || url })); toast.success("Photo uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };
  const reorderPhotos = (from, to) => {
    if (from == null || from === to) return;
    setForm((s) => { const arr = [...(s.photos || [])]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); return { ...s, photos: arr, hero_photo: arr[0] || "" }; });
  };
  const makeHero = (i) => reorderPhotos(i, 0);
  const dropFiles = async (e) => {
    e.preventDefault(); setDropActive(false);
    const files = [...(e.dataTransfer?.files || [])].filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    let ok = 0;
    for (const f of files) {
      try { const url = await uploadImage(f, "/lm/upload"); setForm((s) => ({ ...s, photos: [...(s.photos || []), url], hero_photo: s.hero_photo || url })); ok++; }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} uploaded`);
  };
  const uploadPlan = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const d = await uploadFile(f, "/lm/upload-doc"); setForm((s) => ({ ...s, floor_plans: [...(s.floor_plans || []), { url: d.url, name: d.name }] })); toast.success("Floor plan added"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };
  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 200 * 1024 * 1024) { toast.error("Video must be 200MB or smaller"); e.target.value = ""; return; }
    setUploadingVideo(true); setVideoProgress(0);
    try {
      const d = f.size > 90 * 1024 * 1024
        ? await uploadFileChunked(f, "/lm/upload-video/chunk", setVideoProgress)
        : await uploadFile(f, "/lm/upload-video");
      set("video_walkthrough", d.url);
      toast.success("Video walkthrough uploaded");
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingVideo(false); setVideoProgress(0); e.target.value = ""; }
  };
  const uploadDoc = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const d = await uploadFile(f, "/lm/upload-doc"); setForm((s) => ({ ...s, documents: [...(s.documents || []), { url: d.url, name: d.name }] })); toast.success("Document added"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };
  const copy = () => { navigator.clipboard.writeText(publicUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); };
  const generateAI = async () => {
    if (!form.address_line) { toast.error("Add an address first so the AI has something to work with."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/lm/ai/description", {
        address_line: form.address_line, city: form.city, state: form.state, property_type: form.property_type,
        price: num(form.price), beds: num(form.beds), baths: num(form.baths), sqft: num(form.sqft),
        year_built: form.year_built, features: form.features || [],
      });
      setForm((f) => ({ ...f, description: data.description || f.description, features: data.features?.length ? data.features : f.features }));
      toast.success("AI description generated — review & save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  return (
    <div data-testid="listing-editor" className="max-w-5xl mx-auto">
      <button data-testid="back-to-listings" onClick={() => navigate("/listings")} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] mb-6"><ArrowLeft className="w-4 h-4" /> All listings</button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#8a8a86] font-semibold">Property Websites</div>
          <h1 className="font-serif text-4xl tracking-tight text-[#0A0A0A] mt-1">{form.address_line || "Untitled Listing"}</h1>
          <div className="text-sm text-[#575755]">{form.city ? `${form.city}, ` : ""}{form.state} {form.zip_code}</div>
          {form.last_edited_by && (
            <div data-testid="listing-last-edited" className="mt-1.5 text-xs text-[#a3a099]">
              Last edited by {form.last_edited_by} · {new Date(form.last_edited_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={del} data-testid="delete-listing-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#e0bcbc] text-[#992B2B] text-sm font-semibold hover:bg-[#F6E4E4]"><Trash2 className="w-4 h-4" /> Delete</button>
          <button onClick={() => save()} disabled={saving} data-testid="save-listing-btn" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312]"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      {/* Website card */}
      <div className="bg-[#1F3C88] rounded-sm p-5 mb-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm font-semibold tracking-wide">PROPERTY WEBSITE</div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <span className={form.status === "published" ? "text-[#8FE3B0]" : "text-white/50"}>{form.status === "published" ? "Published" : "Draft"}</span>
              <button data-testid="publish-toggle" onClick={togglePublish} className={`w-11 h-6 rounded-full transition-colors ${form.status === "published" ? "bg-[#F7941D]" : "bg-white/20"} relative`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.status === "published" ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </label>
            <select data-testid="listing-stage-select" value={form.listing_stage} onChange={(e) => save({ listing_stage: e.target.value })} className="bg-[#152A61] border border-[#2E4C94] rounded-sm px-3 py-1.5 text-sm text-white focus:outline-none">
              <option value="coming_soon">Coming Soon</option><option value="for_sale">For Sale</option><option value="under_contract">Under Contract</option><option value="sold">Sold</option>
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer" title="Pin this listing to the top of your agent page">
              <span className={form.featured ? "text-[#F7941D]" : "text-white/50"}>Featured</span>
              <button data-testid="feature-toggle" onClick={() => save({ featured: !form.featured })} className={`w-11 h-6 rounded-full transition-colors ${form.featured ? "bg-[#F7941D]" : "bg-white/20"} relative`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.featured ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </label>
          </div>
        </div>
        {form.listing_stage === "sold" && (
          <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="sold-details">
            <label className="text-xs text-white/70">Sold Price
              <input data-testid="field-sold-price" type="number" value={form.sold_price ?? ""} onChange={(e) => set("sold_price", e.target.value)} onBlur={() => save()} placeholder={form.price || ""}
                className="block mt-1 w-40 bg-[#152A61] border border-[#2E4C94] rounded-sm px-3 py-1.5 text-sm text-white focus:outline-none" />
            </label>
            <label className="text-xs text-white/70">Sold Date
              <input data-testid="field-sold-date" type="date" value={form.sold_date || ""} onChange={(e) => save({ sold_date: e.target.value })}
                className="block mt-1 bg-[#152A61] border border-[#2E4C94] rounded-sm px-3 py-1.5 text-sm text-white focus:outline-none [color-scheme:dark]" />
            </label>
          </div>
        )}
        <div className="mt-3 flex items-stretch gap-2">
          <button onClick={copy} data-testid="copy-url-btn" className="shrink-0 px-3 rounded-sm bg-white/10 hover:bg-white/20 text-sm flex items-center gap-1.5">{copied ? <Check className="w-4 h-4 text-[#8FE3B0]" /> : <Copy className="w-4 h-4" />} Copy</button>
          <div className="flex-1 border border-[#2E4C94] rounded-sm px-3 py-2 text-sm text-white/80 truncate bg-[#152A61]" data-testid="public-url">{publicUrl}</div>
          <a href={`/p/${form.slug}`} target="_blank" rel="noreferrer" data-testid="preview-website-link" className="shrink-0 px-3 rounded-sm bg-[#F7941D] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[#E08312]"><ExternalLink className="w-4 h-4" /> Preview</a>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Listing Details">
            <div className="border border-[#E5E5E2] rounded-sm p-4 mb-4 bg-[#F8FAFE]">
              <div className="text-xs font-semibold text-[#575755] mb-2">Import from Zillow, Realtor.com or Homes.com</div>
              <div className="flex gap-2">
                <input data-testid="import-url-input" className={inp} value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://www.zillow.com/homedetails/..." />
                <button type="button" data-testid="import-btn" onClick={doImport} disabled={importing} className="shrink-0 inline-flex items-center gap-2 px-5 rounded-sm bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#222] disabled:opacity-60"><Download className="w-4 h-4" /> {importing ? "Importing…" : "Import"}</button>
              </div>
              <p className="text-xs text-[#8a8a86] mt-2">Pulls address, price, beds/baths, sqft, description, features and photos. Review before saving.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><span className={lbl}>Address</span><input data-testid="field-address" className={inp} value={form.address_line || ""} onChange={(e) => { set("address_line", e.target.value); if (!slugEdited) set("slug", slugify(e.target.value)); }} /></div>
              <div><span className={lbl}>City</span><input data-testid="field-city" className={inp} value={form.city || ""} onChange={(e) => set("city", e.target.value)} /></div>
              <div><span className={lbl}>State</span><input className={inp} value={form.state || ""} onChange={(e) => set("state", e.target.value)} /></div>
              <div><span className={lbl}>ZIP</span><input className={inp} value={form.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)} /></div>
              <div><span className={lbl}>Price (USD)</span><input data-testid="field-price" type="number" className={inp} value={form.price ?? ""} onChange={(e) => set("price", e.target.value)} /></div>
              <div><span className={lbl}>Property Type</span><input className={inp} value={form.property_type || ""} onChange={(e) => set("property_type", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
              <div><span className={lbl}>Beds</span><input type="number" className={inp} value={form.beds ?? ""} onChange={(e) => set("beds", e.target.value)} /></div>
              <div><span className={lbl}>Baths</span><input type="number" className={inp} value={form.baths ?? ""} onChange={(e) => set("baths", e.target.value)} /></div>
              <div><span className={lbl}>Sq Ft</span><input type="number" className={inp} value={form.sqft ?? ""} onChange={(e) => set("sqft", e.target.value)} /></div>
              <div><span className={lbl}>Year Built</span><input className={inp} value={form.year_built || ""} onChange={(e) => set("year_built", e.target.value)} /></div>
            </div>
            <div className="mt-4"><span className={lbl}>URL Slug</span><input data-testid="field-slug" className={inp} value={form.slug || ""} onChange={(e) => { setSlugEdited(true); set("slug", e.target.value); }} /></div>
          </Card>

          <Card title="Description">
            <div className="flex justify-end mb-2">
              <button type="button" onClick={generateAI} disabled={aiLoading} data-testid="ai-description-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F3C88] text-white text-xs font-semibold hover:bg-[#152A61] disabled:opacity-60">
                <Sparkles className="w-3.5 h-3.5" /> {aiLoading ? "Writing…" : "Generate with AI"}
              </button>
            </div>
            <textarea data-testid="field-description" rows={6} className={inp} value={form.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="Describe the property… or let AI draft it." />
          </Card>

          <Card title="Features">
            <div className="flex gap-2">
              <input data-testid="feature-input" className={inp} value={featInput} onChange={(e) => setFeatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} placeholder="e.g. Chef's kitchen" />
              <button type="button" onClick={addFeature} data-testid="add-feature-btn" className="px-3 rounded-sm border border-[#E5E5E2] hover:bg-[#F0EFEC]"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {(form.features || []).map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-[#F0EFEC] text-[#575755] text-sm rounded-full pl-3 pr-2 py-1">{f}
                  <button onClick={() => set("features", form.features.filter((_, idx) => idx !== i))} className="text-[#8a8a86] hover:text-[#992B2B]"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          </Card>

          <Card title="Photos">
            <div className="flex gap-2">
              <label data-testid="photo-upload-label" className="shrink-0 inline-flex items-center gap-1.5 px-3 rounded-sm bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] cursor-pointer">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" hidden data-testid="photo-upload-input" onChange={uploadPhoto} />
              </label>
              <input data-testid="photo-input" className={inp} value={photoInput} onChange={(e) => setPhotoInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPhoto())} placeholder="…or paste an image URL or a photo gallery link" />
              <button type="button" onClick={addPhoto} disabled={importingPhotos} data-testid="add-photo-btn" className="px-3 rounded-sm border border-[#E5E5E2] hover:bg-[#F0EFEC] disabled:opacity-60">{importingPhotos ? <span className="text-xs font-semibold px-1">Importing…</span> : <Plus className="w-4 h-4" />}</button>
            </div>
            <p className="text-xs text-[#8a8a86] mt-3 mb-2">Paste a photographer's gallery link (e.g. NextDoor Photos) to pull in every photo at once, or a direct image URL. The first photo is the hero image shown at the top of the website. Drag to reorder · click a photo to make it the hero · drop image files below to upload.</p>
            <div
              data-testid="photo-dropzone"
              onDragOver={(e) => { e.preventDefault(); if ((e.dataTransfer?.types || []).includes("Files")) setDropActive(true); }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDropActive(false); }}
              onDrop={dropFiles}
              className={`grid grid-cols-3 sm:grid-cols-4 gap-2 rounded-sm p-2 border-2 border-dashed transition-colors duration-200 ${dropActive ? "border-[#F7941D] bg-[#FBEAE1]" : "border-[#E5E5E2]"}`}
            >
              {(form.photos || []).map((p, i) => (
                <div
                  key={i}
                  draggable
                  data-testid={`photo-thumb-${i}`}
                  onDragStart={() => setDragIdx(i)}
                  onDragEnd={() => setDragIdx(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files?.length) { dropFiles(e); } else { reorderPhotos(dragIdx, i); } setDragIdx(null); }}
                  onClick={() => makeHero(i)}
                  className={`relative h-20 rounded-sm overflow-hidden border-2 cursor-grab active:cursor-grabbing ${i === 0 ? "border-[#F7941D]" : "border-transparent"} ${dragIdx === i ? "opacity-40" : ""}`}
                >
                  <img src={p} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" />
                  <span onClick={(e) => { e.stopPropagation(); setForm((s) => { const arr = s.photos.filter((_, idx) => idx !== i); return { ...s, photos: arr, hero_photo: arr[0] || "" }; }); }} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 cursor-pointer"><X className="w-3 h-3" /></span>
                  {i === 0 && <span className="absolute bottom-1 left-1 bg-[#F7941D] text-white text-[9px] px-1.5 rounded">HERO</span>}
                </div>
              ))}
              {(form.photos || []).length === 0 && (
                <div className="col-span-3 sm:col-span-4 py-8 text-center text-sm text-[#8a8a86]">Drop image files here or use Upload above.</div>
              )}
            </div>
          </Card>

          <Card title="Media · Video, 3D Tour & Floor Plans">
            <div className="space-y-4">
              <div><span className={lbl}>Video Tour URL (YouTube, Vimeo, etc.)</span><input data-testid="field-video-url" className={inp} value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="https://www.youtube.com/watch?v=…" /></div>
              <div><span className={lbl}>3D Walkthrough URL (Matterport, etc.)</span><input data-testid="field-tour-url" className={inp} value={form.tour_3d_url || ""} onChange={(e) => set("tour_3d_url", e.target.value)} placeholder="https://my.matterport.com/show/?m=…" /></div>
              <div>
                <span className={lbl}>Floor Plans</span>
                <div className="flex flex-wrap gap-3 mt-1">
                  {(form.floor_plans || []).map((p, i) => (
                    <div key={i} data-testid={`floor-plan-${i}`} className="relative w-28 h-28 rounded-sm overflow-hidden border border-[#E5E5E2] bg-[#F0EFEC] flex items-center justify-center">
                      {(/\.pdf($|\?)/i.test(p.url) || (p.name || "").toLowerCase().endsWith(".pdf"))
                        ? <div className="flex flex-col items-center text-[#575755] px-2 text-center"><FileText className="w-7 h-7 mb-1" /><span className="text-[10px] leading-tight truncate w-24">{p.name || "Plan.pdf"}</span></div>
                        : <img src={p.url} alt="" className="w-full h-full object-cover" />}
                      <button type="button" onClick={() => set("floor_plans", form.floor_plans.filter((_, idx) => idx !== i))} data-testid={`remove-plan-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label data-testid="add-plan-label" className="w-28 h-28 rounded-sm border-2 border-dashed border-[#E5E5E2] hover:border-[#F7941D] flex flex-col items-center justify-center text-[#8a8a86] hover:text-[#F7941D] cursor-pointer transition-colors">
                    <Upload className="w-6 h-6 mb-1" /><span className="text-xs font-medium">Add plans</span>
                    <input type="file" accept="image/*,application/pdf" hidden data-testid="plan-upload-input" onChange={uploadPlan} />
                  </label>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Documents · Disclosures & Brochures">
            <p className="text-xs text-[#8a8a86] mb-3">Upload PDFs buyers can download from the property website.</p>
            <div className="space-y-2">
              {(form.documents || []).map((d, i) => (
                <div key={i} data-testid={`document-${i}`} className="flex items-center gap-3 border border-[#E5E5E2] rounded-sm px-3 py-2">
                  <FileText className="w-4 h-4 text-[#1F3C88] shrink-0" />
                  <a href={d.url} target="_blank" rel="noreferrer" className="text-sm text-[#1F3C88] hover:underline truncate flex-1">{d.name || "Document.pdf"}</a>
                  <button type="button" onClick={() => set("documents", form.documents.filter((_, idx) => idx !== i))} data-testid={`remove-document-${i}`} className="text-[#8a8a86] hover:text-[#992B2B]"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <label data-testid="add-pdf-label" className="mt-3 w-full rounded-sm border-2 border-dashed border-[#E5E5E2] hover:border-[#F7941D] flex items-center justify-center gap-2 py-4 text-[#8a8a86] hover:text-[#F7941D] cursor-pointer transition-colors">
              <Upload className="w-4 h-4" /><span className="text-sm font-medium">Add PDF</span>
              <input type="file" accept="application/pdf" hidden data-testid="pdf-upload-input" onChange={uploadDoc} />
            </label>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Assigned Agent">
            <select data-testid="agent-select" className={inp} value={form.agent_id || ""} onChange={(e) => set("agent_id", e.target.value)}>
              <option value="">No agent</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Card>
          <Card title="Live Preview">
            <div className="rounded-sm overflow-hidden border border-[#E5E5E2]">
              {(form.photos || [])[0] ? <img src={(form.photos || [])[0]} alt="" className="w-full h-28 object-cover" /> : <div className="h-28 bg-[#F0EFEC]" />}
              <div className="p-3"><div className="font-serif text-xl text-[#1F3C88]">{currency(form.price)}</div><div className="text-xs text-[#575755]">{form.address_line}</div></div>
            </div>
          </Card>

          <ShareCard property={form} publicUrl={publicUrl} />

          <ReportCard publicUrl={publicUrl} />

          <Card title="Marketing Flyers">
            <p className="text-xs text-[#8a8a86] mb-3">Print-ready PDF flyers, branded for the team. Save the listing first — flyers use the saved record.</p>
            <button type="button" data-testid="flyer-coming-soon" onClick={() => downloadFlyer("coming-soon")} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] transition-colors"><Download className="w-4 h-4" /> Coming Soon flyer</button>
            <div className="mt-4 border-t border-[#E5E5E2] pt-4">
              <span className={lbl}>Open House details</span>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input type="date" data-testid="flyer-oh-date" className={inp} value={ohDate} onChange={(e) => setOhDate(e.target.value)} />
                <input type="time" data-testid="flyer-oh-time" className={inp} value={ohTime} onChange={(e) => setOhTime(e.target.value)} />
              </div>
              <button type="button" data-testid="flyer-open-house" onClick={() => downloadFlyer("open-house")} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] transition-colors"><Download className="w-4 h-4" /> Open House flyer</button>
            </div>
          </Card>

          <Card title="Video Walkthrough">
            <p className="text-xs text-[#8a8a86] mb-3">Upload a walkthrough video buyers can watch on the property website — then download it or share it to social media.</p>
            {form.video_walkthrough ? (
              <div className="space-y-3">
                <video data-testid="video-walkthrough-preview" src={videoAbs} poster={(form.photos || [])[0] || form.hero_photo || undefined} controls preload="metadata" className="w-full rounded-sm border border-[#E5E5E2] bg-black" />
                <div className="flex gap-2">
                  <a data-testid="video-download" href={videoAbs} download className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] transition-colors"><Download className="w-4 h-4" /> Download</a>
                  <button type="button" data-testid="video-remove" onClick={() => set("video_walkthrough", "")} className="px-4 py-2.5 rounded-md border border-[#E5E5E2] text-[#992B2B] text-sm font-semibold hover:bg-[#FBEAE1] transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#8a8a86] mt-2 mb-2">Share to</div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    ["facebook", "Facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(videoAbs)}`],
                    ["x", "X", `https://twitter.com/intent/tweet?text=${encodeURIComponent("Video walkthrough: " + (form.address_line || "this home"))}&url=${encodeURIComponent(videoAbs)}`],
                    ["sms", "Text", `sms:?&body=${encodeURIComponent("Video walkthrough: " + videoAbs)}`],
                    ["email", "Email", `mailto:?subject=${encodeURIComponent("Video walkthrough")}&body=${encodeURIComponent("Watch the walkthrough: " + videoAbs)}`],
                  ].map(([key, label, href]) => (
                    <a key={key} data-testid={`video-share-${key}`} href={href} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md border border-[#E5E5E2] text-[#575755] hover:border-[#1F3C88] hover:text-[#1F3C88] transition-colors">
                      <span className="text-xs font-medium">{label}</span>
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <label data-testid="video-upload-label" className="w-full rounded-sm border-2 border-dashed border-[#E5E5E2] hover:border-[#F7941D] flex items-center justify-center gap-2 py-6 text-[#8a8a86] hover:text-[#F7941D] cursor-pointer transition-colors">
                <Upload className="w-4 h-4" /><span className="text-sm font-medium">{uploadingVideo ? (videoProgress ? `Uploading… ${videoProgress}%` : "Uploading…") : "Upload video walkthrough"}</span>
                <input type="file" accept="video/*" hidden data-testid="video-upload-input" onChange={uploadVideo} disabled={uploadingVideo} />
              </label>
            )}
            <p className="text-xs text-[#a3a099] mt-2">MP4, MOV or WebM · up to 200MB.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (<div className="bg-white border border-[#E5E5E2] rounded-sm p-5"><h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">{title}</h3>{children}</div>);
}
